import Keycloak, { KeycloakConfig } from "keycloak-js";
import { computed, makeAutoObservable, runInAction, when } from "mobx";
import { setItem } from "../utils/secureStorageValueHandler";
import { RootStore } from "./RootStore";
import dayjs from "dayjs";

export class KeycloakStore {
  rootStore: RootStore;
  loading = true;
  error = false;
  keycloak: Keycloak.KeycloakInstance | null = null;
  logoutTimeout: any = null;
  logoutWarningTimeout: any = null;
  logoutWarningIsActive: boolean = false;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
      logoutTimeout: false,
    });
    this.rootStore = rootStore;
    this.loadUser();
  }

  @computed
  get refreshTokenExpiry() {
    if (
      !this.keycloak ||
      !this.keycloak.refreshTokenParsed ||
      !this.keycloak.refreshTokenParsed.exp
    ) {
      return null;
    }
    return dayjs(this.keycloak.refreshTokenParsed.exp * 1000);
  }

  @computed
  get isAuthenticated() {
    return this.keycloak?.authenticated ?? false;
  }

  /**
   * Initializes keycloak instance. Reroutes to login page.
   */
  async loadUser() {
    this.loading = true;
    await when(() => this.rootStore.configStore.config !== null);

    const config: KeycloakConfig = {
      url: this.rootStore.configStore.config!.keycloak.keycloakHost,
      realm: this.rootStore.configStore.config!.keycloak.keycloakRealm,
      clientId: this.rootStore.configStore.config!.keycloak.keycloakClientId,
    };

    const keycloak = new Keycloak(config);

    const initialized = await keycloak.init({
      onLoad: "check-sso",
      responseMode: "query",
    });
    runInAction(() => {
      this.keycloak = keycloak;
      this.loading = false;
      if (this.isAuthenticated && !initialized) {
        this.error = true;
      }
    });
  }

  login(idpHint: string | null) {
    idpHint =
      idpHint || this.rootStore.configStore.config!.keycloak.keycloakIdpHint;
    this.keycloak?.login({
      idpHint: idpHint,
    });
  }

  logout() {
    setItem("userLogout", true);
    this.keycloak?.logout({
      redirectUri: `${window.location.origin}/`,
    });
  }

  /**
   * Fetches new access and refresh tokens and resets logout time
   */
  async getRefreshedToken() {
    await when(() => !this.loading);

    if (!this.keycloak) {
      throw Error("Keycloak not loaded");
    }

    await this.keycloak
      .updateToken(5)
      .catch((e) => {
        console.error("Token update failed");
      })
      .then(() => {
        clearTimeout(this.logoutTimeout);
        clearTimeout(this.logoutWarningTimeout);
        runInAction(() => (this.logoutWarningIsActive = false));
        this.logoutTimeout = setTimeout(() => {
          this.logout();
        }, this.getLogoutTimeout());
        this.logoutWarningTimeout = setTimeout(() => {
          runInAction(() => (this.logoutWarningIsActive = true));
        }, this.getLogoutTimeout() - 300 * 1000);
      });

    return this.keycloak.token;
  }

  /**
   * Gets time until current refresh token expiry in milliseconds
   */
  getLogoutTimeout(): number {
    if (!this.refreshTokenExpiry) {
      return 0;
    }

    return this.refreshTokenExpiry.diff(dayjs(), "milliseconds");
  }

  /**
   * Opens keycloak access management page
   */
  manageAccount() {
    this.keycloak?.accountManagement();
  }
}
